import React, { useRef, useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Button, CircularProgress, TextField, FormControlLabel, Checkbox, MenuItem } from '@material-ui/core'
import Drawer, { DrawerAction } from 'components/shared/Drawer'
import { Toast } from 'components/shared/Toaster'
import strings from 'strings'
import { previewPortalEmail } from 'actions/client'

// The application emails that use the product name, in the order the applicant goes through them
const PREVIEW_EMAILS = [
    { key: 'receipt', label: 'To applicant: application form received', note: 'Sent right after the applicant sends the form. Steps come from this portal\'s setup.' },
    { key: 'new-application', label: 'To lender: new application received', note: 'Sent to the lender right after the applicant sends the form.' },
    { key: 'submitted', label: 'To applicant: application submitted', note: 'Sent when the applicant clicks Submit in the portal.' },
    { key: 'submission', label: 'To lender: application submitted', note: 'Sent to the lender when the applicant clicks Submit.' },
]

const ClientPortalDrawer = ({ open, onClose, onSubmit, loading = false, mode = 'create', initialData = null }) => {
    const fileInputRef = useRef(null)
    const [fileName, setFileName] = useState('')
    const [logoPreview, setLogoPreview] = useState('')

    const isEditMode = mode === 'edit'
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewHtml, setPreviewHtml] = useState('')
    const [previewFailed, setPreviewFailed] = useState(false)
    const [previewEmail, setPreviewEmail] = useState(PREVIEW_EMAILS[0].key)

    const { control, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
        defaultValues: {
            pageName: '',
            subdomain: '',
            homePageUrl: '',
            productName: '',
            termsOfServiceUrl: '',
            authorizationText: '',
            base64Logo: '',
            demoMode: false,
            businessAndLegalDisclosures: false,
        }
    })

    // Populate form with initial data when editing
    useEffect(() => {
        if (open && isEditMode && initialData) {
            reset({
                pageName: initialData.pageName || '',
                subdomain: initialData.subdomain || '',
                homePageUrl: initialData.homePageUrl || '',
                productName: initialData.productName || '',
                termsOfServiceUrl: initialData.termsOfServiceUrl || '',
                authorizationText: initialData.authorizationText || '',
                base64Logo: initialData.base64Logo || '',
                demoMode: initialData.demoMode || false,
                businessAndLegalDisclosures: initialData.businessAndLegalDisclosures || false,
            })
            if (initialData.base64Logo) {
                setLogoPreview(initialData.base64Logo)
                setFileName('Current logo')
            }
        } else if (open && !isEditMode) {
            reset({
                pageName: '',
                subdomain: '',
                homePageUrl: '',
                productName: '',
                termsOfServiceUrl: '',
                authorizationText: '',
                base64Logo: '',
                demoMode: false,
                businessAndLegalDisclosures: false,
            })
            setLogoPreview('')
            setFileName('')
        }
    }, [open, isEditMode, initialData, reset])

    // Only saved portals have steps to preview, so this is edit-only
    const canPreview = isEditMode && initialData?.clientUuid && initialData?.clientFormConfigId
    const productName = watch('productName')

    // Re-render shortly after typing stops, while the preview is open
    useEffect(() => {
        if (!open || !previewOpen || !canPreview) return
        // A request still in flight when the name or email changes must not overwrite the newer preview
        let stale = false
        const timer = setTimeout(() => {
            previewPortalEmail(initialData.clientUuid, initialData.clientFormConfigId, previewEmail, (productName || '').trim())
                .then(res => { if (!stale) { setPreviewHtml(res?.html || ''); setPreviewFailed(false) } })
                .catch(() => { if (!stale) setPreviewFailed(true) })
        }, 400)
        return () => { stale = true; clearTimeout(timer) }
    }, [open, previewOpen, canPreview, productName, previewEmail]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleFileChange = async (e) => {
        const file = e?.target?.files?.[0]
        if (!file) return

        if (!file.type.startsWith('image/')) {
            Toast({ type: 'error', message: 'Please select an image file' })
            return
        }

        if (file.size > 2 * 1024 * 1024) {
            Toast({ type: 'error', message: 'File size must be less than 2MB' })
            return
        }

        try {
            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => resolve(reader.result)
                reader.onerror = reject
                reader.readAsDataURL(file)
            })

            setValue('base64Logo', base64)
            setFileName(file.name)
            setLogoPreview(base64)
        } catch (error) {
            Toast({ type: 'error', message: 'Failed to read file' })
        }
    }

    const handleClearFile = () => {
        setValue('base64Logo', '')
        setFileName('')
        setLogoPreview('')
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const resetForm = () => {
        reset()
        setFileName('')
        setLogoPreview('')
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleClose = () => {
        resetForm()
        setPreviewOpen(false)
        setPreviewHtml('')
        onClose()
    }

    const onFormSubmit = (data) => {
        const filteredData = Object.entries(data).reduce((acc, [key, value]) => {
            if (value !== '' && value !== null && value !== undefined) {
                if (typeof value === 'boolean') {
                    if (value === true) {
                        acc[key] = value
                    }
                } else if (typeof value === 'string' && value.trim() !== '') {
                    acc[key] = value.trim()
                } else if (value) {
                    acc[key] = value
                }
            }
            return acc
        }, {})

        // Always sent, even blank: the backend keeps the saved name when the field is missing,
        // so a cleared field has to arrive as '' to bring back the default wording
        filteredData.productName = (data.productName || '').trim()

        onSubmit(filteredData)
    }

    return (
        <Drawer
            title={isEditMode ? "Edit Client Portal" : "Create Client Portal"}
            open={open}
            onClose={handleClose}
            width="medium"
        >
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <div style={{ padding: '1.5rem' }} className="mb-5 pb-5">
                    <Controller
                        name="pageName"
                        control={control}
                        rules={{ required: 'Page Name is required' }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Page Name *"
                                margin="normal"
                                variant="outlined"
                                error={!!errors.pageName}
                                helperText={errors.pageName?.message}
                            />
                        )}
                    />
                    <Controller
                        name="subdomain"
                        control={control}
                        rules={{ required: 'Subdomain is required' }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Subdomain *"
                                margin="normal"
                                variant="outlined"
                                error={!!errors.subdomain}
                                helperText={errors.subdomain?.message}
                            />
                        )}
                    />
                    <Controller
                        name="homePageUrl"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Home Page URL"
                                margin="normal"
                                variant="outlined"
                            />
                        )}
                    />
                    <Controller
                        name="productName"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Product Name"
                                margin="normal"
                                variant="outlined"
                                helperText="Shown in applicant emails, e.g. 'OA AdvancePay'. Leave blank for 'factoring'."
                            />
                        )}
                    />
                    {canPreview && (
                        <div style={{ marginBottom: '0.5rem' }}>
                            <Button size="small" color="primary" type="button" onClick={() => setPreviewOpen(v => !v)}>
                                {previewOpen ? 'Hide email preview' : 'Preview emails'}
                            </Button>
                            {previewOpen && (
                                <div style={{ marginTop: '0.5rem', border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                                    <div style={{ padding: '0.75rem', borderBottom: '1px solid #e0e0e0' }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            label="Email"
                                            value={previewEmail}
                                            onChange={e => {
                                                // Blank until the new email loads, so the old one isn't mistaken for it
                                                setPreviewHtml('')
                                                setPreviewFailed(false)
                                                setPreviewEmail(e.target.value)
                                            }}
                                        >
                                            {PREVIEW_EMAILS.map(email => (
                                                <MenuItem key={email.key} value={email.key}>{email.label}</MenuItem>
                                            ))}
                                        </TextField>
                                        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.54)' }}>
                                            {PREVIEW_EMAILS.find(email => email.key === previewEmail)?.note}
                                        </div>
                                    </div>
                                    {previewFailed && (
                                        <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem', color: '#b00020' }}>
                                            The preview couldn't be loaded. It will retry when you edit the name.
                                        </div>
                                    )}
                                    <iframe
                                        title="Email preview"
                                        srcDoc={previewHtml}
                                        sandbox="allow-same-origin"
                                        style={{ width: '100%', height: '640px', border: 0, display: 'block' }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <Controller
                        name="termsOfServiceUrl"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Terms of Service URL"
                                margin="normal"
                                variant="outlined"
                            />
                        )}
                    />
                    <Controller
                        name="authorizationText"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="Authorization Text"
                                margin="normal"
                                variant="outlined"
                                multiline
                                rows={3}
                            />
                        )}
                    />

                    {/* Logo File Upload */}
                    <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(0, 0, 0, 0.54)', fontSize: '0.875rem' }}>
                            Logo
                        </label>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => fileInputRef.current?.click()}
                                type="button"
                            >
                                {logoPreview ? 'Change File' : 'Choose File'}
                            </Button>
                            {fileName && (
                                <>
                                    <span style={{ color: '#666' }}>{fileName}</span>
                                    <Button
                                        size="small"
                                        color="secondary"
                                        onClick={handleClearFile}
                                        type="button"
                                    >
                                        Clear
                                    </Button>
                                </>
                            )}
                        </div>
                        <small style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
                            Accepted formats: PNG, JPG, GIF. Max size: 2MB
                        </small>

                        {logoPreview && (
                            <div style={{
                                marginTop: '1rem',
                                padding: '1rem',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                backgroundColor: 'var(--bg-canvas)'
                            }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(0, 0, 0, 0.54)', fontSize: '0.75rem' }}>
                                    Preview
                                </label>
                                <img
                                    src={logoPreview}
                                    alt="Logo preview"
                                    style={{
                                        maxWidth: '200px',
                                        maxHeight: '100px',
                                        objectFit: 'contain',
                                        display: 'block',
                                        marginBottom: '0.5rem'
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    <div style={{ marginTop: '1rem' }}>
                        <Controller
                            name="demoMode"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Demo Mode"
                                />
                            )}
                        />
                    </div>
                    <div style={{ marginTop: '0.5rem' }}>
                        <Controller
                            name="businessAndLegalDisclosures"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Business and Legal Disclosures"
                                />
                            )}
                        />
                    </div>
                </div>

                <DrawerAction>
                    <Button
                        onClick={handleClose}
                        color="default"
                        style={{ marginRight: '1rem' }}
                        type="button"
                    >
                        {strings.CANCEL}
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            isEditMode ? 'Update Portal' : 'Create Portal'
                        )}
                    </Button>
                </DrawerAction>
            </form>
        </Drawer>
    )
}

export default ClientPortalDrawer